import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useCurrentApplication } from '../useCurrentApplication'
import { useCallback, useState } from 'react'
import { getAppAccessesColumnsV2 } from './AppAccessesTable/getAppAccessesColumnsV2'
import { toast } from 'sonner'
import { DataTable } from '@/components/ui/DataTable'
import { Button } from '@/components/shadcn/button'
import { TablePageContainer } from '@/components/ui/DataTable/PageTableContainer'

type MessagesApplicationsApplicationAccess = RestSchemas['Messages.Applications.ApplicationAccess']

export type ApplicationAccessItem = {
  accessId: number
  nodeType: string
  nodeId: string
  nodeDescription: string
}

type TAccessMap = Map<number, ApplicationAccessItem>
type TRoleMap = Map<number, ApplicationAccessRole>
type TRoleAccessMap = Map<number, Map<number, boolean>>

export type ApplicationAccessRole = {
  roleId: number
  roleName: string
  roleDescription: string
}

export type TNodeTypeFilter = {
  count: number
  nodeType: string
}

export function AppAccessesPageContent() {
  const { applicationId } = useCurrentApplication()
  const { getApplicationsApiGetAccesses, postApplicationsApiSetAccesses } = useRestClient()
  const [checkedMap, setCheckedMap] = useState<TRoleAccessMap>(new Map<number, Map<number, boolean>>())

  const selector = useCallback((data: MessagesApplicationsApplicationAccess[]) => {
    const rolesMap: TRoleMap = new Map<number, ApplicationAccessRole>()
    const rolesAccessCountMap = new Map<number, number>()
    const accessMap: TAccessMap = new Map<number, ApplicationAccessItem>()
    const accessAccessCountMap = new Map<number, number>()
    const newCheckedMap: Map<number, Map<number, boolean>> = new Map<number, Map<number, boolean>>()
    const nodeTypeFilters = new Map<string, number>()

    data.forEach((x) => {
      // cols
      if (x.roleId !== undefined && x.roleId !== null && !rolesMap.has(x.roleId!))
        rolesMap.set(x.roleId!, {
          roleId: x.roleId,
          roleName: x.roleName!,
          roleDescription: x.roleDescription!,
        })

      // checks
      if (x.roleId !== undefined && x.roleId !== null && x.id !== undefined) {
        const checkedAccessMap = newCheckedMap.get(x.roleId) ?? new Map<number, boolean>()
        checkedAccessMap.set(x.id!, x.accessGranted ?? false)
        newCheckedMap.set(x.roleId, checkedAccessMap)

        if (x.accessGranted == true) {
          const currentChecks = rolesAccessCountMap.get(x.roleId)
          rolesAccessCountMap.set(x.roleId, (currentChecks ?? 0) + 1)

          const currentChecksAccess = accessAccessCountMap.get(x.id)
          accessAccessCountMap.set(x.id, (currentChecksAccess ?? 0) + 1)
        }
      }

      // rows
      if (
        x.id !== undefined &&
        x.nodeId !== undefined &&
        x.nodeId !== null &&
        x.nodeType !== undefined &&
        x.nodeType !== null
      )
        if (!accessMap.has(x.id)) {
          if (!nodeTypeFilters.has(x.nodeType)) nodeTypeFilters.set(x.nodeType, 1)
          else nodeTypeFilters.set(x.nodeType, nodeTypeFilters.get(x.nodeType)! + 1)

          accessMap.set(x.id!, {
            accessId: x.id!,
            nodeDescription: x.description ?? '',
            nodeId: x.nodeId,
            nodeType: x.nodeType!,
          })
        }
    })

    setCheckedMap(newCheckedMap)

    const ntFilters = Array.from(nodeTypeFilters)
      .sort((a, b) => b[1] - a[1])
      .map((x) => {
        return {
          nodeType: x[0],
          count: x[1],
        } as TNodeTypeFilter
      })

    const applicationAccessList = Array.from(accessMap.values()).sort((a, b) => {
      const cmpCounts = (accessAccessCountMap.get(b.accessId) ?? 0) - (accessAccessCountMap.get(a.accessId) ?? 0)
      return cmpCounts == 0 ? b.nodeId.localeCompare(a.nodeId) : cmpCounts
    })

    const rolesArray = Array.from(rolesMap.values()).sort(
      (a, b) => (rolesAccessCountMap.get(b.roleId) ?? 0) - (rolesAccessCountMap.get(a.roleId) ?? 0)
    )

    const columns = getAppAccessesColumnsV2({ rolesArray, newCheckedMap, setCheckedMap, nodeTypeFilters: ntFilters })

    return {
      columns,
      applicationAccessList,
      nodeTypeFilters: ntFilters,
    }
  }, [])

  const { data } = getApplicationsApiGetAccesses.useSuspenseQuery(
    {
      query: { applicationId },
    },
    {
      select: selector,
    }
  )

  const { mutateAsync: saveAccess, isPending: isSaving } = postApplicationsApiSetAccesses.useMutation()

  const saveAccessSelections = useCallback(() => {
    const proms: Promise<unknown>[] = []
    checkedMap.forEach((v, k) => {
      const accessesForRole = Array.from(v)
        .filter((x) => x[1] == true)
        .map((x) => x[0])

      const prom = saveAccess(
        {
          body: accessesForRole,
          query: {
            applicationId,
            roleId: k,
          },
        },
        {
          onError: (_err) => {
            toast.error(`Error settings accesses for role ${k}`)
          },
        }
      )
      proms.push(prom)
    })

    Promise.all(proms)
      .then(() => {
        toast.success('Saved all roles')
      })
      .catch((_err) => {
        toast.error('Error settings accesses for roles')
      })
      .finally(() => {
        getApplicationsApiGetAccesses.invalidateQueries({ parameters: { query: { applicationId } } })
      })
  }, [applicationId, checkedMap, getApplicationsApiGetAccesses, saveAccess])

  return (
    <TablePageContainer>
      <DataTable
        columns={data.columns}
        data={data.applicationAccessList}
        action={
          <Button className="h-8" size="sm" disabled={isSaving} onClick={saveAccessSelections}>
            Save
          </Button>
        }
      />
    </TablePageContainer>
  )
}
